import {
  mcpDepositFields,
  mintEntitledWorldGrantsOnHandoff,
} from "../../../../../cockpit/components/cleo-code/handoffMint.js";
import { fetchPrimary } from "../environments/primary/fetch";

/**
 * The Desk's World sign-in through its own window's Clerk (Can 31; step 1 of the one-backend plan, 16 Oct 2026).
 *
 * The person signs in in their browser (the hosted page, loginCeremony.ts startNeucleosSignIn; Jason, 17 Oct 2026:
 * "a normal login with the browser, not that pop-up window"). The hosted page posts the World grants and a one-time
 * Clerk sign-in ticket to the Desk's callback; the window redeems the ticket for its own Clerk session
 * (redeemDeskSignInTicket), so the World accepts the window live and Clerk renews it. The window's Clerk is the
 * World's own production instance (the one Cleo Connect already uses).
 *
 * Signed in there while the Desk holds no stored sign-in, the window signs the Desk in itself (selfSignIn): it asks
 * Clerk for a `convex` token, mints the Desk's World grants through the hosted page's own door and code (handoffMint.js,
 * sessionGrants:mintMembershipGrants), and deposits them through the Desk's own login door and callback. Only grants
 * cross to the Desk's server that way; no Clerk token does (loginHttp.ts parseMcpDeposit).
 *
 * WorldClerkSession.tsx mounts inside the Electron ClerkProvider and registers here; this module stays free of React
 * so the sign-in ceremony (loginCeremony.ts) can call it from any surface.
 */

export type LiveState = "checking" | "signed-out" | "accepted" | "refused" | "other-person";

export interface LiveReport {
  readonly state: LiveState;
  readonly identity: string | null;
  readonly actorId: string | null;
  readonly worlds: ReadonlyArray<string>;
  readonly checkedAt: string | null;
  readonly reason: string | null;
}

export interface WindowClerkControls {
  /**
   * Signs the window's Clerk in with a one-time sign-in ticket; true when the window then holds an active session. Given
   * the sign-out epoch the redemption began in, it activates nothing once a sign-out has come since.
   */
  readonly redeem: (ticket: string, epoch?: number) => Promise<boolean>;
  /** Ends the window's Clerk session (Neucleos sign-out ends it with the grants), or only the one named. */
  readonly signOut: (sessionId?: string) => Promise<void>;
  /** W-2b8dcba8: whether the window holds a Clerk session now (redeemDeskSignInTicket takes no ticket while it does). */
  readonly signedIn?: () => boolean;
  /** The current Clerk instance has finished loading; a one-use ticket must wait for it. */
  readonly ready?: () => boolean;
}

let controls: WindowClerkControls | null = null;
// W-2b8dcba8 (Codex review of #2453, round 5): every Neucleos sign-out starts a new epoch, at its very start. A self
// sign-in or a ticket redemption begun before it never deposits or activates after it, so a sign-out is never undone by
// work already under way (a window effect re-run by a new Desk person, a ticket fetched just before).
let signOutEpoch = 0;
// And while a sign-out is under way (from its start until the window's Clerk is ended), nothing signs anyone in, so work
// that starts during it cannot either (Codex review of #2453, round 6).
let signOutsUnderway = 0;
// The SDK's actual session removal is distinct from the bounded logout UI.
// It must finish before another session can be activated in this window.
let clerkSignOutsUnderway = 0;
const SIGN_OUT_BLOCK_MAX_MS = 60_000;
/** The current sign-out epoch: work that captured another must not sign anyone in. */
export const windowSignOutEpoch = (): number => signOutEpoch;
/** Whether a sign-in (begun in `epoch`, if given) must not complete now: a sign-out since, or one under way. */
export const signInBlocked = (epoch: number | undefined): boolean =>
  signOutsUnderway > 0 ||
  clerkSignOutsUnderway > 0 ||
  (epoch !== undefined && epoch !== signOutEpoch);
/**
 * loginCeremony.ts signOutOfNeucleos, first thing: no sign-in begun before this completes after it, and none begins until
 * the returned `end` is called (after the window's Clerk is ended).
 */
export function beginNeucleosSignOut(): () => void {
  signOutEpoch += 1;
  signOutsUnderway += 1;
  let ended = false;
  const end = () => {
    if (ended) return;
    ended = true;
    clearTimeout(bound);
    signOutsUnderway -= 1;
  };
  // Codex round 7: a sign-out whose request never answers does not block every later sign-in until a reload; past this
  // bound the block lifts (the epoch still stops anything begun before the sign-out).
  const bound = setTimeout(end, SIGN_OUT_BLOCK_MAX_MS);
  return end;
}

/** WorldClerkSession registers while it is mounted; it returns the unregister. */
export function registerWindowClerk(next: WindowClerkControls): () => void {
  controls = next;
  return () => {
    if (controls === next) controls = null;
  };
}

/**
 * After a browser sign-in: takes the one-time ticket the hosted page deposited (only the person's own Desk session
 * may, loginHttp.ts) and signs the window's Clerk in with it. False where the window mounts no Clerk (a browser, a build
 * without it), when no ticket is waiting, or when Clerk refused it; the stored grants carry the Desk either way.
 */
export async function redeemDeskSignInTicket(): Promise<boolean> {
  const registered = controls;
  // W-2b8dcba8: a window that holds a Clerk session (as anyone) takes no ticket: Clerk would refuse it and the one-time
  // ticket would be spent. A window signed in as someone else is signed out first by WorldClerkSession (it follows the
  // Desk's sign-in), and the ticket is redeemed then. Overlapping asks need no more: the first takes the ticket, the rest
  // find none and change nothing.
  if (
    !registered ||
    registered.ready?.() === false ||
    registered.signedIn?.() === true ||
    signInBlocked(undefined)
  )
    return false;
  const epoch = signOutEpoch;
  const response = await fetchPrimary("/api/neucleos/desk-sign-in-ticket", {
    method: "POST",
  }).catch(() => null);
  if (!response?.ok) return false;
  const body = (await response.json().catch(() => null)) as { ticket?: unknown } | null;
  if (typeof body?.ticket !== "string" || body.ticket.length === 0) return false;
  // Signed out while the ticket was fetched (or a sign-out is under way): it is not redeemed.
  if (
    signInBlocked(epoch) ||
    controls !== registered ||
    registered.ready?.() === false ||
    registered.signedIn?.() === true
  )
    return false;
  try {
    return await registered.redeem(body.ticket, epoch);
  } catch {
    if (signInBlocked(epoch) || controls !== registered || registered.signedIn?.() === true)
      return false;
    // Do not log a provider error that may contain the one-use ticket.
    await reportLive({
      state: "refused",
      identity: null,
      actorId: null,
      worlds: [],
      checkedAt: null,
      reason: "This window could not finish signing in. Sign in again in Cleo.",
    });
    return false;
  }
}

/** Ends the window's Clerk session; false where the window mounts no Clerk (nothing was ended). */
export async function signOutWindowClerk(sessionId?: string): Promise<boolean> {
  const registered = controls;
  if (!registered) return false;
  // Clerk can announce signed-out before its asynchronous session removal finishes.
  // The account-follow path needs the same barrier as an explicit Neucleos sign-out,
  // or that announcement spends the next person's ticket during the old teardown.
  signOutEpoch += 1;
  clerkSignOutsUnderway += 1;
  try {
    await registered.signOut(sessionId);
    return true;
  } finally {
    clerkSignOutsUnderway -= 1;
  }
}

export async function reportLive(report: LiveReport): Promise<void> {
  await fetchPrimary("/api/neucleos/live", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(report),
  }).catch(() => undefined);
}

export type SelfSignInResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly error: string };

/**
 * The Desk signs itself in to the World from the window's Clerk session: the login door's state and requested engines,
 * the hosted page's own mint, then the Desk's own callback. `convexToken` is the window's Clerk `convex` token.
 */
export async function selfSignIn(input: {
  readonly convexUrl: string;
  readonly convexToken: string;
  readonly identity: string;
  /** W-2b8dcba8: the sign-out epoch the caller began in; a sign-out since stops it before it mints or deposits. */
  readonly epoch?: number;
}): Promise<SelfSignInResult> {
  const signedOutSince = () => signInBlocked(input.epoch);
  if (signedOutSince())
    return { ok: false, error: "the Desk was signed out since this sign-in began" };
  const login = await fetchPrimary("/api/neucleos/login", {
    headers: { accept: "application/json" },
  });
  if (!login.ok) return { ok: false, error: `the Desk's login door answered ${login.status}` };
  const body = (await login.json().catch(() => null)) as { authorizeUrl?: unknown } | null;
  if (typeof body?.authorizeUrl !== "string")
    return { ok: false, error: "the Desk's login door named no sign-in" };
  const authorize = new URL(body.authorizeUrl);
  const state = authorize.searchParams.get("state");
  if (!state) return { ok: false, error: "the Desk's login door named no state" };
  const renewable = authorize.searchParams.get("grant_renewal") === "1";
  const requestedHarnesses = authorize.searchParams.get("grant_harnesses");
  const minted = await mintEntitledWorldGrantsOnHandoff({
    convexUrl: input.convexUrl,
    clerkConvexJwt: input.convexToken,
    ...(requestedHarnesses === null ? {} : { requestedHarnesses }),
    renewable,
  });
  if (!minted.ok) return { ok: false, error: minted.error };
  if (signedOutSince())
    return { ok: false, error: "the Desk was signed out since this sign-in began" };
  const deposit = await fetchPrimary("/api/neucleos/callback", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(
      mcpDepositFields({ state, identity: input.identity, grants: minted.grants, renewable }),
    ),
  });
  if (!deposit.ok) {
    const refused = (await deposit.json().catch(() => null)) as { error?: unknown } | null;
    return {
      ok: false,
      error: `the Desk refused the sign-in (${typeof refused?.error === "string" ? refused.error : deposit.status})`,
    };
  }
  return { ok: true };
}
